import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DataTable from "datatables.net-react";
import DT from "datatables.net-bs5";
import "datatables.net-bs5/css/dataTables.bootstrap5.min.css";
import { useAuth } from "../auth/AuthProvider";

DataTable.use(DT);

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 20;

function parseTableStateFromUrl(defaultSortBy, defaultSortDir, defaultPageSize) {
    const params = new URLSearchParams(window.location.search);

    const page = Number.parseInt(params.get("page") || String(DEFAULT_PAGE), 10);
    const pageSize = Number.parseInt(params.get("pageSize") || String(defaultPageSize), 10);
    const q = params.get("q") || "";
    const sortBy = params.get("sortBy") || defaultSortBy;
    const sortDir = params.get("sortDir") === "asc" ? "asc" : defaultSortDir;

    return {
        page: Number.isInteger(page) && page > 0 ? page : DEFAULT_PAGE,
        pageSize: Number.isInteger(pageSize) && pageSize > 0 ? pageSize : defaultPageSize,
        q,
        sortBy,
        sortDir
    };
}

function getColumnIndexByData(columns, dataKey, fallbackIndex) {
    const index = columns.findIndex((column) => column?.data === dataKey);
    return index >= 0 ? index : fallbackIndex;
}

function defaultMapResponse(data) {
    return {
        items: data?.items || [],
        total: data?.pagination?.total || 0
    };
}

function initializeBootstrapTooltips(wrapper) {
    const Tooltip = window.bootstrap?.Tooltip;
    if (!wrapper || !Tooltip) {
        return;
    }

    wrapper.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((element) => {
        Tooltip.getOrCreateInstance(element);
    });
}

function disposeBootstrapTooltips(wrapper) {
    const Tooltip = window.bootstrap?.Tooltip;
    if (!wrapper || !Tooltip) {
        return;
    }

    wrapper.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((element) => {
        const instance = Tooltip.getInstance(element);
        instance?.dispose?.();
    });
}

function ServerSideDataTable({
    columns,
    fetchPage,
    mapResponse = defaultMapResponse,
    defaultSortBy,
    defaultSortDir = "desc",
    defaultSortIndex = 0,
    defaultPageSize = DEFAULT_PAGE_SIZE,
    tableClassName = "table table-striped table-hover align-middle mb-0",
    language,
    errorMessage = "Impossible de charger les données pour l'instant.",
    registerDomHandlers,
    onAfterDraw
}) {
    const { withAuth } = useAuth();
    const [error, setError] = useState("");
    const wrapperRef = useRef(null);

    const initialTableState = useMemo(() => {
        return parseTableStateFromUrl(defaultSortBy, defaultSortDir, defaultPageSize);
    }, [defaultPageSize, defaultSortBy, defaultSortDir]);

    const updateUrlWithTableState = useCallback((state) => {
        const params = new URLSearchParams(window.location.search);
        params.set("page", String(state.page));
        params.set("pageSize", String(state.pageSize));

        if (state.q) {
            params.set("q", state.q);
        } else {
            params.delete("q");
        }

        params.set("sortBy", state.sortBy || defaultSortBy);
        params.set("sortDir", state.sortDir === "asc" ? "asc" : "desc");

        const nextUrl = `${window.location.pathname}?${params.toString()}${window.location.hash}`;
        window.history.replaceState(null, "", nextUrl);
    }, [defaultSortBy]);

    useEffect(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) {
            return undefined;
        }

        initializeBootstrapTooltips(wrapper);

        const cleanup = registerDomHandlers?.({
            wrapper,
            setError
        });

        return () => {
            if (typeof cleanup === "function") {
                cleanup();
            }

            disposeBootstrapTooltips(wrapper);
        };
    }, [registerDomHandlers]);

    return (
        <>
            {error && <div className="alert alert-warning py-2">{error}</div>}

            <div className="card shadow-sm">
                <div className="card-body" ref={wrapperRef}>
                    <DataTable
                        className={tableClassName}
                        columns={columns}
                        options={{
                            processing: true,
                            serverSide: true,
                            searching: true,
                            ordering: true,
                            pageLength: initialTableState.pageSize,
                            displayStart: (initialTableState.page - 1) * initialTableState.pageSize,
                            search: {
                                search: initialTableState.q
                            },
                            lengthMenu: [10, 20, 50, 100],
                            order: [[getColumnIndexByData(columns, initialTableState.sortBy, defaultSortIndex), initialTableState.sortDir]],
                            ajax: async (dtParams, callback) => {
                                const start = Number(dtParams?.start ?? 0);
                                const length = Number(dtParams?.length ?? defaultPageSize);
                                const page = Math.floor(start / length) + 1;

                                const q = dtParams?.search?.value?.trim() || "";
                                const order = dtParams?.order?.[0];
                                const orderColumn = dtParams?.columns?.[order?.column ?? defaultSortIndex];
                                const sortBy = typeof orderColumn?.data === "string" && orderColumn.data
                                    ? orderColumn.data
                                    : defaultSortBy;
                                const sortDir = order?.dir === "asc" ? "asc" : "desc";

                                updateUrlWithTableState({
                                    page,
                                    pageSize: length,
                                    q,
                                    sortBy,
                                    sortDir
                                });

                                try {
                                    setError("");
                                    const response = await withAuth((token) => fetchPage(token, {
                                        page,
                                        pageSize: length,
                                        q,
                                        sortBy,
                                        sortDir
                                    }));

                                    const mapped = mapResponse(response);

                                    callback({
                                        draw: dtParams?.draw,
                                        recordsTotal: mapped.total,
                                        recordsFiltered: mapped.total,
                                        data: mapped.items
                                    });
                                } catch (err) {
                                    if (err?.code !== "session_revoked") {
                                        setError(errorMessage);
                                    }

                                    callback({
                                        draw: dtParams?.draw,
                                        recordsTotal: 0,
                                        recordsFiltered: 0,
                                        data: []
                                    });
                                }

                                requestAnimationFrame(() => {
                                    const wrapper = wrapperRef.current;
                                    initializeBootstrapTooltips(wrapper);
                                    onAfterDraw?.({ wrapper, setError });
                                });
                            },
                            language: language || {
                                search: "Rechercher :",
                                lengthMenu: "Afficher _MENU_ lignes",
                                info: "_START_ à _END_ sur _TOTAL_",
                                infoEmpty: "0 à 0 sur 0",
                                zeroRecords: "Aucune donnée.",
                                processing: "Chargement...",
                                paginate: {
                                    previous: "Précédent",
                                    next: "Suivant"
                                }
                            }
                        }}
                    />
                </div>
            </div>
        </>
    );
}

export default ServerSideDataTable;
